import { createComputed } from "zustand-computed";
import { memoize } from "proxy-memoize";
import BigNumber from "bignumber.js";
import { nCr } from "utilities/number";
import { isMarketInactive } from "@/features/betslip/utils/marketStatus";
import { isMarketOpenForBets } from "utilities/markets";
import { pickMultiLegs } from "@/features/betslip/utils/pickMultiLegs";
import { areRelated } from "@/features/betslip/utils/rules";
import { getSrmCandidate } from "@/features/betslip/utils/srmEligibility";
import {
  isPermittedToBet,
  isSelectionDisallowed,
  summariseBetPermissions,
  toBettingPermissions,
} from "@/features/betslip/utils/permissions";
import {
  isHeldOverlay,
  isSemSelection,
  isSingleSelection,
} from "@/features/betslip/utils/typeGuards";
import type {
  BetSelection,
  MarketStatus,
  PossibleCombination,
} from "@/features/betslip/types";
import type {
  BetslipComputed,
  BetslipState,
  BetslipStore,
  UpdatesSyncTarget,
  QuaddieStatusTarget,
} from "@/features/betslip/stores/types";
import { ALLOW_CROSS_PRODUCT_MULTI } from "@/features/betslip/utils/flags";

const selectActiveSelections = memoize((state: BetslipState) =>
  Object.values(state.selections),
);

const selectMultiableSelections = memoize((state: BetslipState) =>
  selectActiveSelections(state).filter((s) => s.isMultiable),
);

// Has auth answered yet? Live access reads false either way, so nothing can
// explain a blanked price until this is true.
const selectHasSession = (state: BetslipState) => state.permissions !== null;

export const selectCanViewLiveMarkets = (state: BetslipState) =>
  state.permissions?.viewLiveMarkets === "GRANTED";

// Live selections this user can't bet. Derived from what sync already writes,
// keyed by slip id - a SEM is view-only as soon as one leg is.
const selectLiveViewOnly = memoize((state: BetslipState) => {
  const liveViewOnly: Record<string, boolean> = {};

  const isLiveOutcome = (outcomeId: string, eventId: string) => {
    const status = state.marketStatuses[outcomeId];
    // Suspending drops the LIVE status, so the betting type has to cover that
    // gap. Settled and cancelled lose it too, and there the permission isn't
    // what's stopping the bet - the market is. No status yet means it hasn't
    // synced, not that it's gone.
    if (status !== undefined && !isMarketOpenForBets(status)) return false;
    return (
      state.marketBettingTypes[outcomeId] === "Live" ||
      status === "LIVE" ||
      state.eventStatuses[eventId] === "LIVE"
    );
  };

  for (const selection of selectActiveSelections(state)) {
    const { outcomeId, eventId } = selection;

    const isLive = isLiveOutcome(outcomeId, eventId);
    const hasLiveLeg =
      selection.kind === "sem" &&
      selection.legs.some((leg) => isLiveOutcome(leg.outcomeId, eventId));

    liveViewOnly[outcomeId] =
      !selectCanViewLiveMarkets(state) && (isLive || hasLiveLeg);
  }
  return liveViewOnly;
});

// What the slip can bet right now: a trading market, and a live one only with
// the permission. A SEM needs every leg. False and missing both read as
// blocked, so a selection this hasn't seen yet stays that way until it has.
// Just the market - `isCountableSelection` below adds the rest.
const selectBettable = memoize((state: BetslipState) => {
  const liveViewOnly = selectLiveViewOnly(state);
  const bettable: Record<string, boolean> = {};

  const isTrading = (outcomeId: string) =>
    !isMarketInactive(state.marketStatuses[outcomeId]);

  for (const selection of selectActiveSelections(state)) {
    const { outcomeId } = selection;

    const everyLegTrading =
      selection.kind !== "sem" ||
      selection.legs.every((leg) => isTrading(leg.outcomeId));

    bettable[outcomeId] =
      !liveViewOnly[outcomeId] && isTrading(outcomeId) && everyLegTrading;
  }

  return bettable;
});

// What the session can't bet on. Derived from the selections, so it can't fall
// behind them. No permissions yet means no answer yet.
const selectNotPermittedOutcomeIds = memoize((state: BetslipState) => {
  const ids = new Set<string>();
  if (!state.permissions) return ids;

  const permissions = toBettingPermissions(state.permissions);
  for (const selection of selectActiveSelections(state)) {
    if (!isPermittedToBet(permissions, selection, state.userEventFilters)) {
      ids.add(selection.outcomeId);
    }
  }
  return ids;
});

// Selections a permission change has invalidated - eg: an exotic after the exotic
// permission goes. Unlike the not-permitted set these don't stay in the
// slip; `useAutoPruneByPermissions` removes them.
const selectDisallowedOutcomeIds = memoize((state: BetslipState) => {
  const ids = new Set<string>();
  if (!state.permissions) return ids;

  const flags = summariseBetPermissions(state.permissions);
  for (const selection of selectActiveSelections(state)) {
    if (isSelectionDisallowed(selection, flags)) ids.add(selection.outcomeId);
  }
  return ids;
});

// Singles are the standalone bets. SEM bets (Same Game / Same Race
// Multi) get their own sections, so they're excluded here.
const selectSinglesSectionSelections = memoize((state: BetslipState) =>
  selectActiveSelections(state).filter((s) => s.kind !== "sem"),
);

// Same Game Multi: a SEM built from one sports/esports event.
const selectSgmSectionSelections = memoize((state: BetslipState) =>
  selectActiveSelections(state).filter(
    (s) => s.kind === "sem" && s.hub !== "racing",
  ),
);

// Same Race Multi: a SEM built from one race.
const selectSrmSectionSelections = memoize((state: BetslipState) =>
  selectActiveSelections(state).filter(
    (s) => s.kind === "sem" && s.hub === "racing",
  ),
);

// The "Create SRM" opportunity: non-null only when every single in the slip is
// an eligible same-race SRM leg. Availability and pricing are layered on in the
// suggestion hook, which owns the time- and network-dependent parts.
const selectSrmCandidate = memoize((state: BetslipState) =>
  getSrmCandidate(selectActiveSelections(state), selectBettable(state)),
);

const comboHasResult = (state: BetslipState, comboType: string): boolean => {
  const combo = state.stakes.combinations[comboType];
  return combo?.betId !== undefined || combo?.rejected !== undefined;
};

const selectHasComboResult = memoize((state: BetslipState) =>
  Object.keys(state.stakes.combinations).some((type) =>
    comboHasResult(state, type),
  ),
);

// Status always syncs; the price only when this user could act on it.
const selectUpdatesSyncTargets = memoize((state: BetslipState) => {
  const liveViewOnly = selectLiveViewOnly(state);

  return selectActiveSelections(state).flatMap<UpdatesSyncTarget>(
    (selection) => {
      const trackOdds = !liveViewOnly[selection.outcomeId];

      if (selection.kind === "sem") {
        return selection.legs.map((leg) => ({
          outcomeId: leg.outcomeId,
          eventId: selection.eventId,
          statusId: leg.outcomeId,
          sem: selection,
          trackOdds,
        }));
      }

      if (selection.kind === "exotic") {
        const runner = selection.positions.flat()[0];
        if (!runner) return [];
        return [
          {
            outcomeId: runner.outcomeId,
            eventId: selection.eventId,
            statusId: selection.outcomeId,
            sem: undefined,
            trackOdds: false,
          },
        ];
      }

      if (selection.kind === "quaddie") return [];

      return [
        {
          outcomeId: selection.outcomeId,
          eventId: selection.eventId,
          statusId: selection.outcomeId,
          sem: undefined,
          trackOdds,
        },
      ];
    },
  );
});

// Every SEM in the slip has to re-price when its legs change.
const selectSemPricingIds = memoize((state: BetslipState) =>
  selectActiveSelections(state).flatMap((s) =>
    s.kind === "sem" ? [s.outcomeId] : [],
  ),
);

const selectQuaddieStatusTargets = memoize((state: BetslipState) =>
  selectActiveSelections(state).flatMap<QuaddieStatusTarget>((selection) =>
    selection.kind === "quaddie"
      ? [{ outcomeId: selection.outcomeId, marketId: selection.marketId }]
      : [],
  ),
);

// Raw set — every outcomeId in multiActiveLegs that resolves to a selection.
// Used only for token-slot eligibility, so the user's preferred pick set
// survives deactivation cycles.
const selectAllMultiLegEntries = memoize((state: BetslipState) => {
  const legs: BetSelection[] = [];
  for (const outcomeId of state.multiActiveLegs) {
    const selection = state.selections[outcomeId];
    if (selection) legs.push(selection);
  }
  return legs;
});

// Effective set — same as above but legs whose event is in excludedEventIds
// are dropped. This is THE canonical multi-leg list; submission, analytics,
// odds, payout, validity, combinations all read from it.
export const selectMultiLegs = memoize((state: BetslipStore) => {
  const legs: BetSelection[] = [];
  for (const outcomeId of state.multiActiveLegs) {
    const selection = state.selections[outcomeId];
    if (!selection) continue;
    if (state.excludedEventIds.has(selection.eventId)) continue;
    legs.push(selection);
  }
  return legs;
});

const selectPotentialMultiLegs = memoize((state: BetslipState) =>
  pickMultiLegs(selectMultiableSelections(state), {
    allowCrossProduct: ALLOW_CROSS_PRODUCT_MULTI,
  }),
);

// A multi is all-or-nothing, so a leg with no visible price blanks the combined
// odds and payout too.
const selectMultiHasLiveViewOnlyLeg = memoize((state: BetslipState) => {
  const liveViewOnly = selectLiveViewOnly(state);
  return selectMultiLegs(state).some((leg) => liveViewOnly[leg.outcomeId]);
});

const selectMultiOdds = memoize((state: BetslipState) => {
  if (selectMultiHasLiveViewOnlyLeg(state)) return 0;
  const legs = selectMultiLegs(state);
  return legs.length > 0 ? legs.reduce((acc, s) => acc * s.odds, 1) : 0;
});

// The multi product with any pending leg changes applied. Equals multiOdds
// when nothing is pending, so a differing value is what signals a change.
const selectMultiNewOdds = memoize((state: BetslipState) => {
  if (selectMultiHasLiveViewOnlyLeg(state)) return 0;
  const legs = selectMultiLegs(state);
  if (legs.length === 0) return 0;
  return legs.reduce(
    (acc, s) => acc * (state.oddsChanges[s.outcomeId] ?? s.odds),
    1,
  );
});

export const selectCanFormMulti = (state: BetslipStore): boolean =>
  selectMultiLegs(state).length >= 2;

// A stake on a multi that can't form is parked, not spent — nothing counts
// it until the legs are back.
export const selectPlaceableMultiStake = (state: BetslipStore): number =>
  selectCanFormMulti(state) ? state.stakes.multi : 0;

// Multi payout subtracts the stake when Bonus Cash funds the multi, matching
// the legacy promo-aware calculatePayout.
// The multi stake to show, in cents — the placed amount after a partial
// acceptance, otherwise the entered stake.
const selectMultiDisplayStake = memoize((state: BetslipState) => {
  const result = state.multiResult;
  const partial =
    result?.status === "accepted" ? result.partialStake : undefined;
  return partial ?? state.stakes.multi;
});

// Payout previews the pending product (multiNewOdds), so it moves in step
// with the odds display while a leg change is awaiting acceptance — matching
// how a single's payout tracks its own pending price.
const selectMultiPayout = memoize((state: BetslipState) => {
  const stake = selectMultiDisplayStake(state);
  const result = state.multiResult;
  const acceptedOdds = result?.status === "accepted" ? result.odds : undefined;
  const gross = ((acceptedOdds ?? selectMultiNewOdds(state)) * stake) / 100;
  return state.promoCash.multi ? gross - stake / 100 : gross;
});

const selectMultiHasSP = memoize((state: BetslipState) =>
  selectMultiLegs(state).some((l) => l.isSP),
);

// Non-countable and live view-only selections drop out of the footer total:
// a live view-only stake was entered before the market went live and can't be
// submitted now.
const selectCombinedSinglesStake = memoize((state: BetslipState) => {
  const liveViewOnly = selectLiveViewOnly(state);
  return selectActiveSelections(state).reduce(
    (sum, sel) =>
      !liveViewOnly[sel.outcomeId] && isCountableSelection(state, sel)
        ? sum + (state.stakes.singles[sel.outcomeId] ?? 0)
        : sum,
    0,
  );
});

const selectCombinedSinglesPayout = memoize((state: BetslipState) => {
  const liveViewOnly = selectLiveViewOnly(state);
  return selectActiveSelections(state).reduce((sum, sel) => {
    if (liveViewOnly[sel.outcomeId]) return sum;
    if (!isCountableSelection(state, sel)) return sum;
    const stake = state.stakes.singles[sel.outcomeId] ?? 0;
    const isPromo = state.promoCash.singles[sel.outcomeId] ?? false;
    const gross = (sel.odds * stake) / 100;
    return sum + (isPromo ? gross - stake / 100 : gross);
  }, 0);
});

// The stake a single card should show, in cents. Normally the amount
// the user entered, but after a partial acceptance only part of the
// stake was matched, so the placed amount is shown instead. Consumers
// render this directly and don't need to know a partial happened.
const selectSingleDisplayStakes = memoize((state: BetslipState) => {
  const stakes: Record<string, number> = {};
  for (const sel of selectActiveSelections(state)) {
    const result = state.selectionResults[sel.outcomeId];
    const partial =
      result?.status === "accepted" ? result.partialStake : undefined;
    stakes[sel.outcomeId] = partial ?? state.stakes.singles[sel.outcomeId] ?? 0;
  }
  return stakes;
});

// Per-single payout, promo- and pending-odds-adjusted. Uses the pending price
// when one is in flight (mirrors getCurrentOdds / the legacy
// selectPayoutForSelection); the combined total above intentionally uses the
// baked price, matching legacy. Driven by the display stake so a partial
// acceptance's payout reflects the amount actually placed.
const selectSinglePayouts = memoize((state: BetslipState) => {
  const displayStakes = selectSingleDisplayStakes(state);
  const liveViewOnly = selectLiveViewOnly(state);
  const payouts: Record<string, number> = {};
  for (const sel of selectActiveSelections(state)) {
    if (liveViewOnly[sel.outcomeId]) {
      payouts[sel.outcomeId] = 0;
      continue;
    }
    const stake = displayStakes[sel.outcomeId] ?? 0;
    const isPromo = state.promoCash.singles[sel.outcomeId] ?? false;
    const result = state.selectionResults[sel.outcomeId];
    // Only priced bets - exotics and quaddies go out at a placeholder price.
    const isPriced = isSingleSelection(sel) || isSemSelection(sel);
    const acceptedOdds =
      isPriced && result?.status === "accepted" ? result.odds : undefined;
    const odds = acceptedOdds ?? state.oddsChanges[sel.outcomeId] ?? sel.odds;
    const gross = (odds * stake) / 100;
    payouts[sel.outcomeId] = isPromo ? gross - stake / 100 : gross;
  }
  return payouts;
});

const selectHasOddsChanges = memoize(
  (state: BetslipState) => Object.keys(state.oddsChanges).length > 0,
);

// A multi leg whose market has gone inactive blocks the whole multi — it's
// all-or-nothing, so the stake stays locked until that leg leaves the multi.
// A SEM leg counts as dead if any of its own legs are. Scoped to the legs
// actually in the multi, so deactivating the dead leg clears the block and the
// remaining legs become stakeable again.
const selectMultiHasInactiveLeg = memoize((state: BetslipState) => {
  const bettable = selectBettable(state);
  return selectMultiLegs(state).some((leg) => !bettable[leg.outcomeId]);
});

// A pending odds change on a multi leg holds the multi stake until the user
// accepts the new price. Scoped to the multi's legs so a change on an unrelated
// single doesn't freeze the multi.
const selectMultiHasPendingOdds = memoize((state: BetslipState) =>
  selectMultiLegs(state).some(
    (leg) => state.oddsChanges[leg.outcomeId] !== undefined,
  ),
);

// An event is blocked when it isn't already in the multi and none of its legs
// can join without conflicting with a live leg elsewhere. Scored across the
// whole slip in one pass — blockedness is a property of the slip, not the
// group — reusing the canonical live-leg set so a deactivated event's lingering
// pick never counts as a conflict.
const selectMultiBlockedEventIds = memoize((state: BetslipState) => {
  const multiLegs = selectMultiLegs(state);
  const liveEventIds = new Set(multiLegs.map((leg) => leg.eventId));

  const groups = new Map<string, BetSelection[]>();
  for (const selection of selectMultiableSelections(state)) {
    const existing = groups.get(selection.eventId);
    if (existing) existing.push(selection);
    else groups.set(selection.eventId, [selection]);
  }

  const blocked = new Set<string>();
  for (const [eventId, bets] of groups) {
    if (liveEventIds.has(eventId)) continue;
    const otherLive = multiLegs.filter((leg) => leg.eventId !== eventId);
    const canJoin = bets.some((bet) =>
      otherLive.every(
        (other) =>
          !areRelated(bet, other, {
            allowCrossProduct: ALLOW_CROSS_PRODUCT_MULTI,
          }),
      ),
    );
    if (!canJoin) blocked.add(eventId);
  }
  return blocked;
});

// The market can take this bet and no price change is waiting on the user.
const isPriceAndMarketBettable = (
  state: BetslipState,
  selection: BetSelection,
): boolean => {
  if (state.oddsChanges[selection.outcomeId] !== undefined) return false;
  return selectBettable(state)[selection.outcomeId] ?? false;
};

// A selection can go into a submission when it isn't sitting on a rejection,
// has no unaccepted odds change, and is bettable. Anything else is excluded
// rather than blocking the rest of the slip. `isSubmittableSelection` narrows
// it once more, with what the server said about this user.
export const isCountableSelection = (
  state: BetslipState,
  selection: BetSelection,
) =>
  state.selectionResults[selection.outcomeId]?.status !== "rejected" &&
  isPriceAndMarketBettable(state, selection);

/**
 * A selection the slip can actually submit right now: its market can take a
 * bet and the logged-in user is permitted to bet on it. A not-permitted or
 * live view-only selection stays visible (with its own warning) but never
 * counts towards a submittable bet, so it can't block the ones around it.
 * Both are permission checks, deliberately kept out of the market status.
 */
export const isSubmittableSelection = (
  state: BetslipState,
  selection: BetSelection,
): boolean =>
  isCountableSelection(state, selection) &&
  !selectNotPermittedOutcomeIds(state).has(selection.outcomeId);

/**
 * Same question for a multi or combo leg. A leg's own single being rejected
 * says nothing about the multi — they're separate bets over the same outcome.
 * The multi's own rejections live in multiLegRejections. Market status and a
 * pending price do still drop a leg.
 */
export const isSubmittableLeg = (state: BetslipState, leg: BetSelection) =>
  isPriceAndMarketBettable(state, leg) &&
  !selectNotPermittedOutcomeIds(state).has(leg.outcomeId);

// Effective market status per selection: its own status, or — for a SEM — the
// first inactive leg, since any inactive leg invalidates the whole SEM.
const selectEffectiveMarketStatuses = memoize((state: BetslipState) => {
  const statuses: Record<string, MarketStatus> = {};
  for (const selection of selectActiveSelections(state)) {
    const own = state.marketStatuses[selection.outcomeId];
    if (own) {
      statuses[selection.outcomeId] = own;
      continue;
    }
    if (selection.kind === "sem") {
      for (const leg of selection.legs) {
        const legStatus = state.marketStatuses[leg.outcomeId];
        if (legStatus) {
          statuses[selection.outcomeId] = legStatus;
          break;
        }
      }
    }
  }
  return statuses;
});

const selectBulkAcceptOddsChangeCount = memoize((state: BetslipState) => {
  if (state.submission.phase !== "idle" || isHeldOverlay(state.overlay)) {
    return 0;
  }

  return selectActiveSelections(state).reduce((count, sel) => {
    if (state.oddsChanges[sel.outcomeId] === undefined) return count;
    if (sel.kind === "exotic") return count;
    if (state.selectionResults[sel.outcomeId]?.status === "rejected") {
      return count;
    }
    // Dead market, or no price on screen to accept.
    if (!selectBettable(state)[sel.outcomeId]) return count;
    return count + 1;
  }, 0);
});

export const selectComboLegSetSubmittable = memoize((state: BetslipState) => {
  const legs = selectMultiLegs(state);
  return legs.length >= 2 && legs.every((leg) => isSubmittableLeg(state, leg));
});

const selectCombinedCombosStake = memoize((state: BetslipState) => {
  let total = 0;
  for (const combo of Object.values(selectSubmittableCombinations(state))) {
    total += combo.total;
  }
  return total;
});

const selectMultiHasLegRejection = memoize((state: BetslipState) =>
  selectMultiLegs(state).some(
    (leg) => state.multiLegRejections[leg.outcomeId] !== undefined,
  ),
);

const selectMultiIsCountable = memoize(
  (state: BetslipState) =>
    state.multiResult?.status !== "rejected" &&
    !selectMultiHasLegRejection(state) &&
    selectComboLegSetSubmittable(state),
);

const selectHasStake = memoize(
  (state: BetslipState) =>
    Object.values(state.stakes.singles).some((s) => s > 0) ||
    selectPlaceableMultiStake(state) > 0 ||
    selectCombinedCombosStake(state) > 0,
);

// A live view-only leg blanks the multi and every combination, so their
// stakes drop out here too - the total would count money that can't be placed.
const selectTotalStake = memoize((state: BetslipState) => {
  const hasLiveViewOnlyLeg = selectMultiHasLiveViewOnlyLeg(state);
  const multiCountable = selectMultiIsCountable(state);
  return (
    selectCombinedSinglesStake(state) +
    (hasLiveViewOnlyLeg
      ? 0
      : (multiCountable ? selectPlaceableMultiStake(state) : 0) +
        selectCombinedCombosStake(state))
  );
});

// The cash the user actually needs on balance: Bonus-Cash-funded stakes
// are paid from the promo balance, so they don't count towards the
// deposit requirement. Counts the same bets as Total Stake.
const selectTotalStakeWithoutPromoCash = memoize((state: BetslipState) => {
  const liveViewOnly = selectLiveViewOnly(state);
  let total = 0;
  for (const selection of selectActiveSelections(state)) {
    const { outcomeId } = selection;
    if (liveViewOnly[outcomeId]) continue;
    if (state.promoCash.singles[outcomeId]) continue;
    if (!isCountableSelection(state, selection)) continue;
    total += state.stakes.singles[outcomeId] ?? 0;
  }
  if (selectMultiHasLiveViewOnlyLeg(state)) return total;
  if (!state.promoCash.multi && selectMultiIsCountable(state)) {
    total += selectPlaceableMultiStake(state);
  }
  for (const combo of Object.values(selectSubmittableCombinations(state))) {
    if (!combo.isPromo) total += combo.total;
  }
  return total;
});

// Product of odds for every size-r combination of legs, enumerated the way
// legacy getCombinations does. Each combination's payout is truncated to
// whole cents individually (see selectComboPayouts), so the sum can't be
// collapsed algebraically.
const combinationOddsProducts = (odds: number[], size: number): number[] => {
  const products: number[] = [];
  const walk = (start: number, chosen: number, product: number) => {
    if (chosen === size) {
      products.push(product);
      return;
    }
    for (let i = start; i <= odds.length - (size - chosen); i++) {
      walk(i + 1, chosen + 1, product * odds[i]);
    }
  };
  walk(0, 0, 1);
  return products;
};

// Combos are formed over the multi legs; a payout is only computable when
// every leg has fixed odds. Mirrors legacy calculatePotentialPayout's TBD
// conditions (SP flag, or the odds-1 SP sentinel).
const selectComboPayoutsTbd = memoize((state: BetslipState) => {
  const legs = selectMultiLegs(state);
  return legs.some(
    (leg) => leg.isSP || (state.oddsChanges[leg.outcomeId] ?? leg.odds) === 1,
  );
});

// Per-size combo payouts in cents (legacy selectComboMultiPayouts /
// calculatePotentialPayout): the type's total stake splits evenly across
// its combinations (half-up); each combination pays product(leg odds) ×
// per-combo stake, truncated to whole cents exactly like legacy; promo
// stakes pay out net of the stake. Unstaked sizes are 0.
const selectComboPayouts = memoize((state: BetslipState) => {
  const payouts: Record<string, number> = {};
  const legs = selectMultiLegs(state);
  const oddsPerLeg = legs.map(
    (leg) => state.oddsChanges[leg.outcomeId] ?? leg.odds,
  );
  // A live view-only leg sits in every combination, so nothing can be priced.
  const blankAll = selectMultiHasLiveViewOnlyLeg(state);

  for (const { type, count } of selectPossibleCombinations(state)) {
    const combo = state.stakes.combinations[type];
    const stake = combo?.total ?? 0;
    if (blankAll || stake <= 0 || count < 1) {
      payouts[type] = 0;
      continue;
    }
    const perComboStake = Math.round(stake / count);
    let gross = 0;
    for (const product of combinationOddsProducts(oddsPerLeg, Number(type))) {
      gross += Math.floor(product * perComboStake);
    }
    payouts[type] = combo?.isPromo ? gross - stake : gross;
  }
  return payouts;
});

const selectCountableMultiPayout = memoize((state: BetslipState) =>
  selectMultiIsCountable(state) && selectCanFormMulti(state)
    ? selectMultiPayout(state)
    : 0,
);

const selectCountableComboPayoutsTotal = memoize((state: BetslipState) => {
  const submittable = selectSubmittableCombinations(state);
  const types = Object.keys(submittable);
  if (types.length === 0 || selectComboPayoutsTbd(state)) {
    return 0;
  }
  const payouts = selectComboPayouts(state);
  let sum = 0;
  for (const type of types) {
    sum += payouts[type] ?? 0;
  }
  return sum;
});

// Whole cents for display, floored like legacy selectPayoutTotal. Singles
// and multi payouts are computed in dollars; combo payouts are already
// whole cents.
const selectTotalPayout = memoize(
  (state: BetslipState) =>
    new BigNumber(
      selectCombinedSinglesPayout(state) + selectCountableMultiPayout(state),
    )
      .multipliedBy(100)
      .dp(0, BigNumber.ROUND_FLOOR)
      .toNumber() + selectCountableComboPayoutsTotal(state),
);

// Sizes above 500 combinations are unstakeble and would flood the wire,
// so they don't render.
const maxRenderableCombinations = 500;

export const selectPossibleCombinations = memoize(
  (state: BetslipStore): PossibleCombination[] => {
    const n = selectMultiLegs(state).length;
    const combos: PossibleCombination[] = [];
    for (let r = 2; r <= n - 1; r++) {
      const count = nCr(n, r);
      if (count > maxRenderableCombinations) continue;
      combos.push({ type: String(r), count });
    }
    return combos;
  },
);

// Combo stakes are keyed by size: "2" is every double, "3" every treble. Take
// a leg out of the multi and a size can stop existing, leaving a stake behind
// for a bet that can no longer be placed. Those get filtered out here.
// Inherits the maxRenderableCombinations cap, so a size past 500 combinations
// is dropped rather than submitted — unreachable today, but a silent one.
export const selectCombinationsForLegCount = memoize((state: BetslipState) => {
  const comboTypes = new Set(
    selectPossibleCombinations(state).map((combo) => combo.type),
  );
  const combos: BetslipState["stakes"]["combinations"] = {};
  for (const [type, combo] of Object.entries(state.stakes.combinations)) {
    if (comboTypes.has(type)) combos[type] = combo;
  }
  return combos;
});

export const selectSubmittableCombinations = memoize((state: BetslipState) => {
  if (!selectComboLegSetSubmittable(state)) return {};

  const combos: BetslipState["stakes"]["combinations"] = {};
  for (const [type, combo] of Object.entries(
    selectCombinationsForLegCount(state),
  )) {
    if (!combo.rejected) {
      combos[type] = combo;
    }
  }
  return combos;
});

const selectIsPayoutTbd = memoize((state: BetslipState) => {
  const stakedHasTbd = selectActiveSelections(state).some((s) => {
    const stake = state.stakes.singles[s.outcomeId] ?? 0;
    if (stake <= 0) return false;
    return s.kind === "exotic" || s.kind === "quaddie" || s.isSP;
  });
  const multiHasTbd =
    selectPlaceableMultiStake(state) > 0 && selectMultiHasSP(state);
  const combosHaveStake = Object.values(
    selectCombinationsForLegCount(state),
  ).some((combo) => combo.total > 0);
  const combosHaveTbd = combosHaveStake && selectComboPayoutsTbd(state);
  return stakedHasTbd || multiHasTbd || combosHaveTbd;
});

// Multi and combos are priced off the same legs, and neither carries a result
// object - a combo only tells us it placed by having a betId.
const hasPlacedMulti = (state: BetslipStore) =>
  state.multiResult?.status === "accepted" ||
  Object.values(state.stakes.combinations).some(
    (combo) => combo.betId !== undefined,
  );

export const computeHasAcceptedBets = (state: BetslipStore) =>
  Object.values(state.selectionResults).some(
    (result) => result.status === "accepted",
  ) || hasPlacedMulti(state);

// is the outcome in an accepted bet? Its card is showing a receipt, so its price is history -
// either the bet went on at it directly, or it was a leg of a multi/combo that placed.
export const isPlacedOutcome = (state: BetslipStore, outcomeId: string) =>
  state.selectionResults[outcomeId]?.status === "accepted" ||
  (hasPlacedMulti(state) &&
    selectMultiLegs(state).some((leg) => leg.outcomeId === outcomeId));

// Review mode needs something actually PLACED. A fully rejected submit
// returns the slip to its editable state with the rejection alerts
// showing, so the user can adjust and go again.
const selectIsReviewing = memoize((state: BetslipState) => {
  if (isHeldOverlay(state.overlay)) return false;
  const hasAcceptedSingle = Object.values(state.selectionResults).some(
    (result) => result.status === "accepted",
  );
  const hasAcceptedMulti = state.multiResult?.status === "accepted";
  const hasAcceptedCombo = Object.values(state.stakes.combinations).some(
    (combo) => combo.betId !== undefined,
  );
  return hasAcceptedSingle || hasAcceptedMulti || hasAcceptedCombo;
});

const isUsedInReview = (state: BetslipState, outcomeId: string) =>
  state.selectionResults[outcomeId] !== undefined;

const selectReviewSingles = memoize((state: BetslipState) =>
  selectSinglesSectionSelections(state).filter((s) =>
    isUsedInReview(state, s.outcomeId),
  ),
);

const selectReviewSgm = memoize((state: BetslipState) =>
  selectSgmSectionSelections(state).filter((s) =>
    isUsedInReview(state, s.outcomeId),
  ),
);

const selectReviewSrm = memoize((state: BetslipState) =>
  selectSrmSectionSelections(state).filter((s) =>
    isUsedInReview(state, s.outcomeId),
  ),
);

const selectUnusedSelections = memoize((state: BetslipState) =>
  selectActiveSelections(state).filter(
    (s) => !isUsedInReview(state, s.outcomeId),
  ),
);

// In review only the combos that got a placement decision (accepted or
// rejected) stay on screen — the per-combo analog of a placed single or
// multi. Unstaked combos never submit, so they drop away rather than
// lingering on the confirmation.
const selectReviewCombinations = memoize((state: BetslipState) =>
  selectPossibleCombinations(state).filter((combo) =>
    comboHasResult(state, combo.type),
  ),
);

// Rejections land on multiResult (bet-level) or multiLegRejections (per leg).
// Keep the card for both, or a leg-rejected multi disappears with no alert.
const selectShowMultiCard = memoize(
  (state: BetslipState) =>
    state.multiResult !== null || selectMultiHasLegRejection(state),
);

// Review changes only which selections and cards each section shows; every
// other field (payouts, odds, stakes, gating) matches the editable slip.
// Projecting the whole review shape here keeps the isReviewing decision to a
// single branch and makes "what review looks like" legible as one unit — a
// new section's review behaviour belongs in this override, not a new ternary.
const projectReview = (
  base: BetslipComputed,
  state: BetslipState,
): BetslipComputed => ({
  ...base,
  singlesSectionSelections: selectReviewSingles(state),
  sgmSectionSelections: selectReviewSgm(state),
  srmSectionSelections: selectReviewSrm(state),
  unusedSelections: selectUnusedSelections(state),
  srmCandidate: null,
  showMultiCard: selectShowMultiCard(state),
  showCombinationsSection: selectHasComboResult(state),
  possibleCombinations: selectReviewCombinations(state),
});

// At the two-tap confirm — and the in-flight submit that follows — the slip
// shows only the cards being submitted to the API: staked, submittable
// singles, the multi card when the multi is staked and valid, and any staked
// combinations. Unstaked or inactive selections drop out entirely; there's no
// unused drawer here the way review keeps one. A multi or combo leg is drawn by
// the multi and combination cards, so it isn't re-listed as a single card —
// doing so would render it as a $0 single, reading as an un-staked bet. What
// stays keys off stake and market status because no submission results exist
// yet.
const projectConfirming = (
  base: BetslipComputed,
  state: BetslipState,
  showMultiCard: boolean,
  showCombinationsSection: boolean,
  submittableCombos: BetslipState["stakes"]["combinations"],
): BetslipComputed => {
  const isStakedSingle = (selection: BetSelection) =>
    (state.stakes.singles[selection.outcomeId] ?? 0) > 0 &&
    isSubmittableSelection(state, selection);
  return {
    ...base,
    singlesSectionSelections:
      base.singlesSectionSelections.filter(isStakedSingle),
    sgmSectionSelections: base.sgmSectionSelections.filter(isStakedSingle),
    srmSectionSelections: base.srmSectionSelections.filter(isStakedSingle),
    unusedSelections: [],
    srmCandidate: null,
    showMultiCard,
    showCombinationsSection,
    possibleCombinations: base.possibleCombinations.filter(
      (combo) => (submittableCombos[combo.type]?.total ?? 0) > 0,
    ),
  };
};

const selectMultiOutcomeIds = memoize((state: BetslipState) =>
  selectAllMultiLegEntries(state).map((l) => l.outcomeId),
);

export const computeIsPlacing = (state: BetslipState): boolean =>
  state.submission.phase === "submitting" || isHeldOverlay(state.overlay);

const computeIsStakeLocked = (state: BetslipState): boolean =>
  computeIsPlacing(state) ||
  selectIsReviewing(state) ||
  state.submission.phase === "confirming";

// A selection takes no stake while the slip is locked, while its market is
// inactive, while an odds change waits to be accepted, or once its bet has
// been accepted.
const selectStakeDisabled = memoize((state: BetslipState) => {
  const isLocked = computeIsStakeLocked(state);
  const bettable = selectBettable(state);
  const disabled: Record<string, boolean> = {};

  for (const { outcomeId } of selectActiveSelections(state)) {
    disabled[outcomeId] =
      isLocked ||
      !bettable[outcomeId] ||
      state.oddsChanges[outcomeId] !== undefined ||
      state.selectionResults[outcomeId]?.status === "accepted";
  }

  return disabled;
});

// The multi's stake, on the same footing as a single's. `multiHasInactiveLeg`
// already counts a live view-only leg as dead.
const selectMultiStakeDisabled = memoize(
  (state: BetslipState) =>
    computeIsStakeLocked(state) ||
    selectMultiLegs(state).length < 2 ||
    selectMultiHasInactiveLeg(state) ||
    selectMultiHasPendingOdds(state),
);

// Combos are built from the multi's legs, so a leg that can't be bet stops them
// all - same rule as the multi's own stake.
const selectCombosStakeDisabled = memoize(
  (state: BetslipState) =>
    computeIsStakeLocked(state) || selectMultiHasInactiveLeg(state),
);

export const computeState = createComputed(
  (state: BetslipState): BetslipComputed => {
    const multiLegs = selectMultiLegs(state);
    const multiOdds = selectMultiOdds(state);
    const hasOddsChanges = selectHasOddsChanges(state);
    const selectionsCount = selectActiveSelections(state).length;
    const hasStake = selectHasStake(state);
    const isReviewing = selectIsReviewing(state);
    const isConfirming = state.submission.phase === "confirming";
    const isSubmitting = state.submission.phase === "submitting";

    // Submission gating: a problem selection (rejection, pending odds,
    // dead market) excludes itself from the payload rather than freezing
    // the whole slip — but a multi or combo is all-or-nothing, since
    // silently dropping one of its legs would place a different bet than
    // the one on screen. The slip can submit as long as at least one
    // product (a countable single, a valid multi, or a valid combo) is
    // ready; the others are dropped from the payload.
    const hasCountableStakedSingle = selectActiveSelections(state).some(
      (selection) =>
        (state.stakes.singles[selection.outcomeId] ?? 0) > 0 &&
        isSubmittableSelection(state, selection),
    );
    const comboLegSetSubmittable = selectComboLegSetSubmittable(state);
    const multiLegSetSubmittable =
      comboLegSetSubmittable && !selectMultiHasLegRejection(state);
    const hasSubmittableMulti =
      selectPlaceableMultiStake(state) > 0 &&
      multiLegSetSubmittable &&
      state.multiResult?.status !== "rejected";
    const submittableCombos = selectSubmittableCombinations(state);
    const hasSubmittableCombo = Object.values(submittableCombos).some(
      (combo) => combo.total > 0,
    );

    const multiCardVisible = selectPotentialMultiLegs(state).length >= 2;
    const hasComboResult = selectHasComboResult(state);
    const possibleCombinations = selectPossibleCombinations(state);

    // Editable-slip shape. The seven fields projectReview() overrides are the
    // only ones that differ under review; everything here is mode-independent.
    const base: BetslipComputed = {
      activeSelections: selectActiveSelections(state),
      multiableSelections: selectMultiableSelections(state),
      multiLegs,
      multiOutcomeIds: selectMultiOutcomeIds(state),
      updatesSyncTargets: selectUpdatesSyncTargets(state),
      semPricingIds: selectSemPricingIds(state),
      quaddieStatusTargets: selectQuaddieStatusTargets(state),
      singlesSectionSelections: selectSinglesSectionSelections(state),
      sgmSectionSelections: selectSgmSectionSelections(state),
      srmSectionSelections: selectSrmSectionSelections(state),
      unusedSelections: [],
      srmCandidate: selectSrmCandidate(state),
      selectionsCount,
      multiOdds,
      multiNewOdds: selectMultiNewOdds(state),
      multiPayout: selectMultiPayout(state),
      multiHasSP: selectMultiHasSP(state),
      canFormMulti: selectCanFormMulti(state),
      combinedSinglesStake: selectCombinedSinglesStake(state),
      combinedSinglesPayout: selectCombinedSinglesPayout(state),
      singleDisplayStakes: selectSingleDisplayStakes(state),
      singlePayouts: selectSinglePayouts(state),
      effectiveMarketStatuses: selectEffectiveMarketStatuses(state),
      stakeDisabled: selectStakeDisabled(state),
      multiStakeDisabled: selectMultiStakeDisabled(state),
      combosStakeDisabled: selectCombosStakeDisabled(state),
      liveViewOnly: selectLiveViewOnly(state),
      hasSession: selectHasSession(state),
      canViewLiveMarkets: selectCanViewLiveMarkets(state),
      notPermittedOutcomeIds: selectNotPermittedOutcomeIds(state),
      disallowedOutcomeIds: selectDisallowedOutcomeIds(state),
      bettable: selectBettable(state),
      multiHasLiveViewOnlyLeg: selectMultiHasLiveViewOnlyLeg(state),
      combinedMultiStake: selectPlaceableMultiStake(state),
      multiDisplayStake: selectMultiDisplayStake(state),
      totalStake: selectTotalStake(state),
      totalStakeWithoutPromoCash: selectTotalStakeWithoutPromoCash(state),
      totalPayout: selectTotalPayout(state),
      comboPayouts: selectComboPayouts(state),
      comboPayoutsTbd: selectComboPayoutsTbd(state),
      canSubmit:
        selectionsCount > 0 &&
        hasStake &&
        state.submission.phase === "idle" &&
        (hasCountableStakedSingle ||
          hasSubmittableMulti ||
          hasSubmittableCombo),
      hasOddsChanges,
      bulkAcceptOddsChangeCount: selectBulkAcceptOddsChangeCount(state),
      multiCardVisible,
      multiHasInactiveLeg: selectMultiHasInactiveLeg(state),
      multiHasPendingOdds: selectMultiHasPendingOdds(state),
      multiBlockedEventIds: selectMultiBlockedEventIds(state),
      possibleCombinations,
      hasComboResult,
      showMultiCard: multiCardVisible,
      showCombinationsSection: possibleCombinations.length > 0,
      isPayoutTbd: selectIsPayoutTbd(state),
      isReviewing,
      isConfirming,
      isPlacing: computeIsPlacing(state),
      isStakeLocked: computeIsStakeLocked(state),
    };

    if (isReviewing) return projectReview(base, state);
    if (isConfirming || isSubmitting) {
      return projectConfirming(
        base,
        state,
        hasSubmittableMulti,
        hasSubmittableCombo,
        submittableCombos,
      );
    }
    return base;
  },
);
